import { Header, Layout1, Sidebar, StackedTitle, useFuzzySearch } from '@ljagis/react-core';
import {
  GeolocationButton,
  Identify,
  Layers,
  LocationSearch,
  Map,
  MapOverlay,
  StyleGallery,
  ZoomInButton,
  ZoomOutButton,
  useGeoJSONLayer,
  useMapFeatureState,
  useMapIdentify,
} from '@ljagis/react-mapping';
import { Box, Divider, IconButton, Tooltip } from '@material-ui/core';
import IdentifyIcon from '@material-ui/icons/FeaturedPlayListOutlined';
import LayersIcon from '@material-ui/icons/LayersOutlined';
import ListIcon from '@material-ui/icons/ListAlt';
import logo from 'assets/img/logo.png';
import { ProjectsList, projectsLayers } from 'components/Projects';
import React, { useEffect, useState } from 'react';
import { ProjectFeature, fetch as fetchProjects } from 'services/projects';
import { searchLocation } from 'services/searchLocation';

const {
  REACT_APP_DITCH_SEARCH_URL: ditchSearchUrl = '',
  REACT_APP_MAPBOX_ACCESS_TOKEN: mapboxAccessToken = '',
} = process.env;

export interface ProjectsPublicPageProps {
  title: string;
  subtitle: string;
  mapStyles: { title: string; previewImage: string; previewImage2x: string; styleUrl: string }[];
}

type sidebars = 'layers' | 'identify' | 'projects';

const ProjectPublicPage: React.FC<ProjectsPublicPageProps> = ({ title, subtitle, mapStyles }) => {
  const [results, setResults] = useState<ProjectFeature[]>([]);

  // Used to highlight when selected
  const [selectedProject, setSelectedProject] = useState<ProjectFeature | null>(null);

  const { setData: setMapProjectData, sourceId: projectSource } = useGeoJSONLayer({
    layers: projectsLayers,
  });

  const {
    layerNames: identifyLayers,
    features: identifyFeatures,
    location: identifyLocation,
    clearFeatures: clearIdentify,
  } = useMapIdentify();

  const { highlightFeature } = useMapFeatureState();

  const { results: fuzzyResults, setQuery } = useFuzzySearch<ProjectFeature>({
    list: results,
    options: { keys: ['properties.projectNumber', 'properties.name'], threshold: 0.2 },
  });

  const [sidebar, setSidebar] = useState<sidebars | undefined>('projects');

  const sidebars: Record<
    sidebars,
    { title: string; content: React.ReactNode; enableSearch?: boolean }
  > = {
    layers: {
      title: 'Layers',
      content: (
        <Box flex={1} display="flex" flexDirection="column" minHeight={0}>
          <Layers flex={1} />
          <Divider />
          <StyleGallery items={mapStyles} />
        </Box>
      ),
    },
    identify: {
      title: 'Feature Details',
      content: (
        <Identify
          flex={1}
          layerNames={identifyLayers}
          features={identifyFeatures}
          location={identifyLocation}
          onClearFeatures={clearIdentify}
        />
      ),
    },
    projects: {
      title: 'Projects',
      enableSearch: true,
      content: <ProjectsList items={fuzzyResults} onItemSelected={setSelectedProject} />,
    },
  };

  // Fetch project data on first load
  useEffect(() => {
    const getProjects = async () => {
      const projectGeoJSON = await fetchProjects();
      setResults(projectGeoJSON.features);
    };
    getProjects();
  }, [setResults]);

  // Update map data on any changes to project search results
  useEffect(() => {
    setMapProjectData({ type: 'FeatureCollection', features: fuzzyResults });
  }, [fuzzyResults, setMapProjectData]);

  // Reset selected project when sidebar changes from projects
  useEffect(() => {
    return () => {
      sidebar === 'projects' && setSelectedProject(null);
    };
  }, [sidebar]);

  // Highlight selected project from list on map
  useEffect(() => {
    if (!selectedProject?.id) {
      return undefined;
    }

    let unhighlight: () => void;

    try {
      unhighlight = highlightFeature({ source: projectSource, id: selectedProject.id });
    } catch (_err) {
      // NOOP
    }

    return () => {
      try {
        unhighlight && unhighlight();
      } catch (_err) {
        // NOOP
      }
    };
  }, [selectedProject, highlightFeature, projectSource]);

  // Open identify sidebar on any new identified features
  useEffect(() => {
    if (!identifyFeatures.length) {
      return;
    }
    setSidebar('identify');
  }, [identifyFeatures]);

  return (
    <Layout1
      height={'100vh'}
      header={
        <Header minHeight={64} color="secondary">
          <img src={logo} alt="" style={{ height: 56, width: 56, marginRight: 16 }} />
          <Box flex="1 1 auto">
            <StackedTitle title={title} subtitle={subtitle} />
          </Box>
          <Tooltip title="Projects">
            <IconButton color="inherit" onClick={() => setSidebar('projects')}>
              <ListIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Layers">
            <IconButton color="inherit" onClick={() => setSidebar('layers')}>
              <LayersIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Feature Details">
            <IconButton color="inherit" edge="end" onClick={() => setSidebar('identify')}>
              <IdentifyIcon />
            </IconButton>
          </Tooltip>
        </Header>
      }
      map={<Map style={{ height: '100%' }} initialMapStyle={mapStyles[0].styleUrl} />}
      mapOverlayTopLeft={[
        <MapOverlay key="locationSearch">
          <Box key={1} minWidth={240}>
            <LocationSearch
              placeholder="Search for an address or ditch"
              onSearch={searchLocation(mapboxAccessToken, ditchSearchUrl)}
            />
          </Box>
        </MapOverlay>,
      ]}
      mapOverlayTopRight={[
        <MapOverlay key="navigation">
          <Box>
            <ZoomInButton />
            <Divider />
            <ZoomOutButton />
          </Box>
        </MapOverlay>,
        <MapOverlay key="geolocation">
          <GeolocationButton />
        </MapOverlay>,
      ]}
      sidebar={
        sidebar && (
          <Sidebar
            height={1}
            title={sidebars[sidebar].title}
            onClose={() => setSidebar(undefined)}
            content={sidebars[sidebar].content}
            enableSearch={sidebars[sidebar].enableSearch}
            onSearch={setQuery}
            onClearSearch={() => setQuery('')}
          />
        )
      }
    />
  );
};

export default ProjectPublicPage;
